//@ts-check
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import * as videosApi from '@/api/videos.api.js';
import { getErrorMessage } from '@/lib/types.js';

/**
 * @typedef {'queued'|'uploading'|'completed'|'failed'} QueueStatus
 */

/**
 * @typedef {object} QueueItem
 * @property {string} localId
 * @property {string} trackingId
 * @property {string} marketplaceId
 * @property {string} mimeType
 * @property {number} sizeBytes
 * @property {number} [durationSec]
 * @property {Blob} blob
 * @property {QueueStatus} status
 * @property {number} progress
 * @property {string} [videoId]
 * @property {string} [uploadUrl]
 * @property {number} [chunkSize]
 * @property {string} [error]
 * @property {boolean} [duplicateWarning]
 */

/**
 * @typedef {{ items: QueueItem[], enqueue: (item: Omit<QueueItem, 'localId'|'status'|'progress'>) => void, retry: (localId: string) => void, dismiss: (localId: string) => void }} UploadQueueValue
 */

const UploadQueueContext = createContext(/** @type {UploadQueueValue|null} */ (null));

let idSeq = 0;
function nextId() {
  idSeq += 1;
  return `uq-${Date.now()}-${idSeq}`;
}

/**
 * @param {QueueItem} item
 * @param {(patch: Partial<QueueItem>) => void} update
 */
async function processItem(item, update) {
  update({ status: 'uploading', progress: 0, error: undefined });

  try {
    let videoId = item.videoId;
    let uploadUrl = item.uploadUrl;
    let chunkSize = item.chunkSize ?? 5 * 1024 * 1024;

    if (!videoId || !uploadUrl) {
      const init = await videosApi.initUpload({
        trackingId: item.trackingId,
        marketplaceId: item.marketplaceId,
        mimeType: item.mimeType,
        sizeBytes: item.sizeBytes,
        durationSec: item.durationSec,
      });
      videoId = init.videoId;
      uploadUrl = init.uploadUrl;
      chunkSize = init.chunkSize ?? chunkSize;
      update({
        videoId,
        uploadUrl,
        chunkSize,
        duplicateWarning: Boolean(init.duplicateWarning),
      });
      if (init.duplicateWarning) {
        toast.message('Duplicate tracking ID — another video already exists for this pack.');
      }
    }

    const total = item.sizeBytes;
    let offset = 0;

    // Try to resume if we already have a session
    if (uploadUrl && offset === 0) {
      try {
        const probe = await videosApi.probeDriveSession(uploadUrl, total);
        if (probe.status === 308 && probe.range) {
          const match = /bytes=0-(\d+)/.exec(probe.range);
          if (match) offset = Number(match[1]) + 1;
        } else if (probe.status === 200 || probe.status === 201) {
          await videosApi.completeUpload(/** @type {string} */ (videoId));
          update({ status: 'completed', progress: 100 });
          return;
        }
      } catch {
        // ignore probe failures; start from 0 or restart below
      }
    }

    while (offset < total) {
      const end = Math.min(offset + chunkSize, total);
      const chunk = item.blob.slice(offset, end);
      const contentRange = `bytes ${offset}-${end - 1}/${total}`;

      let result;
      try {
        result = await videosApi.putDriveChunk(
          /** @type {string} */ (uploadUrl),
          chunk,
          contentRange,
          item.mimeType,
        );
      } catch {
        // CORS / network — proxy through API
        result = await videosApi.uploadChunk(
          /** @type {string} */ (videoId),
          chunk,
          contentRange,
          item.mimeType,
        );
        result = {
          status: result.status,
          range: result.data?.headers?.range,
          body: result.data?.body,
        };
      }

      if (result.status === 308) {
        offset = end;
        update({ progress: Math.round((offset / total) * 100) });
        continue;
      }

      if (result.status === 200 || result.status === 201) {
        offset = total;
        update({ progress: 100 });
        break;
      }

      if (result.status === 404 || result.status === 410) {
        const restarted = await videosApi.restartUpload(/** @type {string} */ (videoId));
        uploadUrl = restarted.uploadUrl;
        chunkSize = restarted.chunkSize ?? chunkSize;
        update({ uploadUrl, chunkSize, videoId: restarted.videoId ?? videoId });
        offset = 0;
        continue;
      }

      throw new Error(`Upload chunk failed (${result.status})`);
    }

    await videosApi.completeUpload(/** @type {string} */ (videoId));
    update({ status: 'completed', progress: 100 });
    toast.success(`Uploaded ${item.trackingId}`);
  } catch (err) {
    update({ status: 'failed', error: getErrorMessage(err) });
    toast.error(`Upload failed: ${getErrorMessage(err)}`);
  }
}

/**
 * @param {{ children: import('react').ReactNode }} props
 */
export function UploadQueueProvider({ children }) {
  const [items, setItems] = useState(/** @type {QueueItem[]} */ ([]));
  const busyRef = useRef(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const updateItem = useCallback(
    /**
     * @param {string} localId
     * @param {Partial<QueueItem>} patch
     */
    (localId, patch) => {
      setItems((prev) => prev.map((it) => (it.localId === localId ? { ...it, ...patch } : it)));
    },
    [],
  );

  const pump = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      while (true) {
        const next = itemsRef.current.find((it) => it.status === 'queued');
        if (!next) break;
        await processItem(next, (patch) => updateItem(next.localId, patch));
      }
    } finally {
      busyRef.current = false;
    }
  }, [updateItem]);

  const enqueue = useCallback(
    /**
     * @param {Omit<QueueItem, 'localId'|'status'|'progress'>} partial
     */
    (partial) => {
      const item = /** @type {QueueItem} */ ({
        ...partial,
        localId: nextId(),
        status: 'queued',
        progress: 0,
      });
      setItems((prev) => [...prev, item]);
      toast.message(`Queued ${partial.trackingId}`);
      queueMicrotask(() => {
        void pump();
      });
    },
    [pump],
  );

  const retry = useCallback(
    /**
     * @param {string} localId
     */
    (localId) => {
      setItems((prev) =>
        prev.map((it) =>
          it.localId === localId
            ? { ...it, status: 'queued', progress: 0, error: undefined, uploadUrl: undefined }
            : it,
        ),
      );
      queueMicrotask(() => {
        void pump();
      });
    },
    [pump],
  );

  const dismiss = useCallback(
    /**
     * @param {string} localId
     */
    (localId) => {
      setItems((prev) => prev.filter((it) => it.localId !== localId));
    },
    [],
  );

  const value = useMemo(
    () => ({ items, enqueue, retry, dismiss }),
    [items, enqueue, retry, dismiss],
  );

  return <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>;
}

/**
 * @returns {{ items: QueueItem[], enqueue: (item: Omit<QueueItem, 'localId'|'status'|'progress'>) => void, retry: (localId: string) => void, dismiss: (localId: string) => void }}
 */
export function useUploadQueue() {
  const ctx = useContext(UploadQueueContext);
  if (!ctx) {
    throw new Error('useUploadQueue must be used within UploadQueueProvider');
  }
  return ctx;
}
